/**
 * Weather: what it is doing, from real observations.
 *
 * Data comes from Open-Meteo (keyless, CORS-open, global), whose "current"
 * block reports the latest conditions for any coordinate: temperature, cloud
 * cover, rain, snowfall, wind, snow depth, and a WMO weather code. This module
 * is the pure half — turning that JSON, or a named preset, into the handful
 * of numbers the renderer needs.
 */

export interface Weather {
  /** Air temperature, °C. */
  temperature: number;
  /** 0 clear .. 1 overcast. */
  cloud: number;
  /** Rain rate, mm/h. */
  rain: number;
  /** Snowfall rate, cm/h. */
  snowfall: number;
  /** 0 clear .. 1 thick fog. */
  fog: number;
  /** Wind speed m/s, and the direction it blows *from*, degrees clockwise from north. */
  wind: number;
  windFrom: number;
  /** Peak gust speed over the last hour, m/s. At least `wind`. */
  gusts: number;
  /** Lying snow, metres. */
  snowDepth: number;
  thunder: boolean;
  /** WMO weather code. */
  code: number;
  description: string;
}

export const CLEAR: Weather = {
  temperature: 15, cloud: 0.1, rain: 0, snowfall: 0, fog: 0, wind: 3, windFrom: 240, gusts: 5,
  snowDepth: 0, thunder: false, code: 0, description: 'clear',
};

/** WMO 4677 code, as Open-Meteo uses it, to words. */
export function describeWeatherCode(code: number): string {
  if (code === 0) return 'clear';
  if (code === 1) return 'mainly clear';
  if (code === 2) return 'partly cloudy';
  if (code === 3) return 'overcast';
  if (code === 45 || code === 48) return 'fog';
  if (code >= 51 && code <= 57) return 'drizzle';
  if (code >= 61 && code <= 67) return code >= 65 ? 'heavy rain' : 'rain';
  if (code >= 71 && code <= 77) return code >= 75 ? 'heavy snow' : 'snow';
  if (code >= 80 && code <= 82) return 'rain showers';
  if (code === 85 || code === 86) return 'snow showers';
  if (code >= 95) return 'thunderstorm';
  return 'unknown';
}

/**
 * Typical rates for a code, used where the measured rate is zero but the
 * code says it is raining — the rate is for the past hour, the code is now.
 */
function codeRates(code: number): { rain: number; snow: number; fog: number } {
  if (code === 45 || code === 48) return { rain: 0, snow: 0, fog: 0.85 };
  if (code >= 51 && code <= 57) return { rain: 0.4, snow: 0, fog: 0.15 };
  if (code >= 61 && code <= 67) return { rain: code >= 65 ? 8 : code >= 63 ? 3 : 1, snow: 0, fog: 0.1 };
  if (code >= 71 && code <= 77) return { rain: 0, snow: code >= 75 ? 3 : code >= 73 ? 1.5 : 0.6, fog: 0.15 };
  if (code >= 80 && code <= 82) return { rain: code === 82 ? 12 : 4, snow: 0, fog: 0.05 };
  if (code === 85 || code === 86) return { rain: 0, snow: code === 86 ? 2.5 : 1, fog: 0.1 };
  if (code >= 95) return { rain: 10, snow: 0, fog: 0.1 };
  return { rain: 0, snow: 0, fog: 0 };
}

/** Typical gust-to-mean ratio over land, for when no gust is reported. */
export const GUST_FACTOR = 1.5;

/** The variables requested from Open-Meteo's `current` block. */
export const OPEN_METEO_VARIABLES = [
  'temperature_2m', 'cloud_cover', 'rain', 'showers', 'snowfall',
  'weather_code', 'wind_speed_10m', 'wind_direction_10m', 'wind_gusts_10m', 'snow_depth',
] as const;

export function openMeteoUrl(lat: number, lon: number): string {
  return `https://api.open-meteo.com/v1/forecast?latitude=${lat.toFixed(3)}&longitude=${lon.toFixed(3)}`
    + `&current=${OPEN_METEO_VARIABLES.join(',')}&wind_speed_unit=ms&timezone=UTC`;
}

export function fromOpenMeteo(json: unknown): Weather {
  const current = (json as { current?: Record<string, number | undefined> }).current ?? {};
  const num = (k: string, fallback: number) => {
    const v = current[k];
    return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
  };
  const code = num('weather_code', 0);
  const typical = codeRates(code);
  const rain = Math.max(num('rain', 0) + num('showers', 0), typical.rain);
  const snowfall = Math.max(num('snowfall', 0), typical.snow);
  const wind = Math.max(0, num('wind_speed_10m', 3));
  return {
    temperature: num('temperature_2m', 15),
    cloud: Math.min(1, Math.max(0, num('cloud_cover', 0) / 100)),
    rain,
    snowfall,
    fog: typical.fog,
    wind,
    windFrom: num('wind_direction_10m', 240),
    // Over land, gusts typically run half as fast again as the mean wind.
    gusts: Math.max(wind, num('wind_gusts_10m', wind * GUST_FACTOR)),
    snowDepth: num('snow_depth', 0),
    thunder: code >= 95,
    code,
    description: describeWeatherCode(code),
  };
}

/** Named weather for `?weather=`, for trying a look without waiting for it. */
export function presetWeather(name: string): Weather | undefined {
  const presets: Record<string, Partial<Weather>> = {
    clear: { cloud: 0.05, code: 0 },
    cloudy: { cloud: 0.7, code: 2 },
    overcast: { cloud: 1, code: 3 },
    rain: { cloud: 0.95, rain: 3, code: 63, wind: 6, gusts: 11 },
    storm: { cloud: 1, rain: 12, code: 95, thunder: true, wind: 14, gusts: 26 },
    snow: { cloud: 0.95, snowfall: 1.5, snowDepth: 0.2, temperature: -3, code: 73, wind: 4, gusts: 7 },
    fog: { cloud: 0.8, fog: 0.85, code: 45, wind: 1, gusts: 1.5 },
    windy: { cloud: 0.45, code: 2, wind: 11, gusts: 19 },
    gale: { cloud: 0.9, rain: 1.5, code: 61, wind: 20, gusts: 32 },
  };
  const p = presets[name];
  if (p === undefined) return undefined;
  const w = { ...CLEAR, ...p };
  return { ...w, description: describeWeatherCode(w.code) };
}

/** The presets by name, in the order the HUD shows them. */
export const PRESET_NAMES = ['clear', 'cloudy', 'overcast', 'rain', 'storm', 'snow', 'fog', 'windy', 'gale'] as const;

/**
 * Weather set by hand (the HUD's sliders) made whole: the WMO code and its
 * words follow from the rates, as a station would report them, so the rest
 * of the game reads it like real weather.
 */
export function simulatedWeather(w: Weather): Weather {
  const code = w.thunder ? 95
    : w.snowfall >= 2.5 ? 75 : w.snowfall >= 1 ? 73 : w.snowfall > 0.05 ? 71
      : w.rain >= 7 ? 65 : w.rain >= 2 ? 63 : w.rain > 0.3 ? 61 : w.rain > 0.05 ? 51
        : w.fog >= 0.5 ? 45
          : w.cloud >= 0.9 ? 3 : w.cloud >= 0.4 ? 2 : w.cloud >= 0.15 ? 1 : 0;
  return { ...w, gusts: Math.max(w.wind, w.gusts), code, description: describeWeatherCode(code) };
}

/**
 * `?wind=` for trying a wind without waiting for one: `speed` in m/s,
 * optionally `,from` in degrees and `,gusts` in m/s, like `?wind=18,270,30`.
 */
export function parseWindParam(value: string): Pick<Weather, 'wind'> & Partial<Weather> | undefined {
  const [speed, from, gusts] = value.split(',').map(Number);
  if (!Number.isFinite(speed) || speed < 0) return undefined;
  return {
    wind: speed,
    gusts: Number.isFinite(gusts) ? Math.max(speed, gusts) : speed * GUST_FACTOR,
    ...(Number.isFinite(from) ? { windFrom: from } : {}),
  };
}
